import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Search, Calendar, Download, Sparkles, ArrowUpDown, Loader2 } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SORT_OPTIONS = [
  { value: "count_desc", label: "Usage count (high→low)" },
  { value: "count_asc",  label: "Usage count (low→high)" },
  { value: "last_used_desc", label: "Last used (newest)" },
  { value: "last_used_asc",  label: "Last used (oldest)" },
  { value: "name_asc",  label: "Name (A→Z)" },
  { value: "name_desc", label: "Name (Z→A)" },
];

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

const AdminAILogs = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [sort, setSort] = useState("count_desc");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (search) params.set("search", search);
      if (sort) params.set("sort", sort);
      const { data } = await api.get(`/admin/ai-logs/summary?${params.toString()}`);
      setRows(data || []);
    } catch { toast.error("Failed to load AI logs"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [sort]);  // eslint-disable-line react-hooks/exhaustive-deps

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (search) params.set("search", search);
      if (sort) params.set("sort", sort);
      const res = await api.get(`/admin/ai-logs/export.xlsx?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-designer-logs-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  const totals = useMemo(() => ({
    users: rows.length,
    generations: rows.reduce((a, r) => a + (r.count || 0), 0),
    orders: rows.reduce((a, r) => a + (r.order_count || 0), 0),
  }), [rows]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-ai-logs-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> Customer AI Logs
        </p>
        <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-2">AI Designer usage report</h1>
        <p className="text-neutral-600 text-sm mt-1">Aggregated per user with filters and Excel export.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="pav-card">
          <p className="text-xs uppercase tracking-wider text-neutral-500">Users</p>
          <p className="font-display text-2xl font-extrabold mt-1">{totals.users}</p>
        </div>
        <div className="pav-card">
          <p className="text-xs uppercase tracking-wider text-neutral-500">Total AI generations</p>
          <p className="font-display text-2xl font-extrabold mt-1">{totals.generations}</p>
        </div>
        <div className="pav-card">
          <p className="text-xs uppercase tracking-wider text-neutral-500">Total orders</p>
          <p className="font-display text-2xl font-extrabold mt-1">{totals.orders}</p>
        </div>
      </div>

      <div className="pav-card p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wider text-neutral-500">Search user</label>
            <div className="mt-1">
              <SearchInput
                value={search}
                onChange={setSearch}
                onEnter={load}
                placeholder="Name or email"
                data-testid="ai-logs-search" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500 flex items-center gap-1"><Calendar className="h-3 w-3" /> From</label>
            <input type="date" className="pav-input mt-1" data-testid="ai-logs-start"
              value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-500">To</label>
            <input type="date" className="pav-input mt-1" data-testid="ai-logs-end"
              value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div className="min-w-[200px]">
            <label className="text-xs uppercase tracking-wider text-neutral-500 flex items-center gap-1"><ArrowUpDown className="h-3 w-3" /> Sort</label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="pav-input mt-1" data-testid="ai-logs-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button onClick={load} disabled={loading} className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="ai-logs-apply">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Apply
          </button>
          <button onClick={downloadXlsx} disabled={downloading || rows.length === 0}
            className="btn-outline text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="ai-logs-export">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Excel
          </button>
        </div>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ai-logs-table">
            <thead>
              <tr className="bg-[#0B1E3F] text-white text-left">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold text-right">AI Designer Usage</th>
                <th className="px-4 py-3 font-semibold text-right">Order Count</th>
                <th className="px-4 py-3 font-semibold">Last Used</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-neutral-500" data-testid="ai-logs-empty">No usage matches the filter.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.user_id} className="border-b border-[#F4F6FB] hover:bg-[#F8FAFC]"
                  data-testid={`ai-logs-row-${r.user_id}`}>
                  <td className="px-4 py-3 font-semibold">{r.user_name || "—"}</td>
                  <td className="px-4 py-3 text-neutral-500">{r.user_email}</td>
                  <td className="px-4 py-3"><span className="text-xs uppercase font-semibold text-neutral-500">{r.user_role}</span></td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-[#0B1E3F]">{r.count}</td>
                  <td className="px-4 py-3 text-right font-mono text-neutral-600">{r.order_count}</td>
                  <td className="px-4 py-3 text-neutral-500">{fmt(r.last_used)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAILogs;
